import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import type { ClawbackAmountLine, ClawbackCurrencyTotal, ClawbackOfficial } from '@/models/tournament';
import { clawbackKindOf, clawbackKindsPresent, formatCurrencyAmount } from './clawbackFormat';

/**
 * CO-921/CO-922 Round 2 (owner: "HR is actively aware that it's done and he actually approved the op").
 *
 * Who loses which amount, one row per official, one line per currency and kind, then the totals per
 * currency. Used by the consent dialog, the bulk import preview and the tournament delete preview, so the
 * same claw-back always reads the same way. Test ids are `${testIdPrefix}-official-{userId}`,
 * `-line-{userId}-{code}-{kind}` (+`-amount`), `-total-{code}` and `-repaid-{userId}`; the spec lane
 * writes against them.
 *
 * Amounts sit in a `dir="ltr"` span so a currency code and its digits never reorder inside Arabic text.
 * Rows use a full border and a tint, never a coloured side stripe (ui-standards.md).
 */

interface ClawbackSummaryListProps {
  officials: ClawbackOfficial[];
  /** Server totals; computed from the lines when absent (the tournament delete preview has none). */
  totals?: ClawbackCurrencyTotal[] | null;
  testIdPrefix: string;
  /** Sentences explaining each kind under the list. Off where the surrounding block already explains it. */
  showKindNotes?: boolean;
}

/** One line per currency and kind: an official on two pay entries in one currency reads as one figure. */
function mergedLines(lines: ClawbackAmountLine[]): ClawbackAmountLine[] {
  const byKey = new Map<string, ClawbackAmountLine>();
  for (const l of lines) {
    const kind = clawbackKindOf(l.kind);
    const key = `${l.currencyId}-${kind}`;
    const row = byKey.get(key);
    if (row) row.amount += l.amount;
    else byKey.set(key, { ...l, kind });
  }
  return [...byKey.values()];
}

function totalsFromLines(officials: ClawbackOfficial[]): ClawbackCurrencyTotal[] {
  const byCurrency = new Map<number, ClawbackCurrencyTotal>();
  for (const o of officials) {
    for (const l of o.lines) {
      const row = byCurrency.get(l.currencyId) ?? { currencyId: l.currencyId, currencyCode: l.currencyCode, pendingEntry: 0, nextPayroll: 0 };
      if (clawbackKindOf(l.kind) === 'pendingEntry') row.pendingEntry += l.amount;
      else row.nextPayroll += l.amount;
      byCurrency.set(l.currencyId, row);
    }
  }
  return [...byCurrency.values()];
}

const ClawbackSummaryList: React.FC<ClawbackSummaryListProps> = ({ officials, totals, testIdPrefix, showKindNotes = true }) => {
  const { t, i18n } = useTranslation('tournament');
  const lang = i18n.language || 'en';
  const shownTotals = useMemo(() => (totals && totals.length > 0 ? totals : totalsFromLines(officials)), [totals, officials]);
  const kinds = useMemo(() => clawbackKindsPresent(officials), [officials]);

  const kindLabel = (kind: string) =>
    clawbackKindOf(kind) === 'pendingEntry' ? t('Removed from pending pay') : t('Deducted on the next payroll');

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {officials.map((o) => (
          <li
            key={o.userId}
            className="rounded-lg border border-border bg-muted/40 px-3 py-2.5"
            data-testid={`${testIdPrefix}-official-${o.userId}`}
          >
            <p className="flex min-w-0 items-center gap-2 text-sm font-semibold text-foreground">
              <Icon name="User" size={14} className="shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 truncate" title={o.fullName || undefined}>{o.fullName || t('common:UnknownUser')}</span>
            </p>
            <dl className="mt-1.5 space-y-1">
              {mergedLines(o.lines).map((l) => {
                const kind = clawbackKindOf(l.kind);
                const lineId = `${testIdPrefix}-line-${o.userId}-${l.currencyCode}-${kind}`;
                return (
                  <div key={`${l.currencyId}-${kind}`} className="flex items-baseline justify-between gap-4" data-testid={lineId}>
                    <dt className="min-w-0 text-sm text-muted-foreground">{kindLabel(kind)}</dt>
                    <dd className="shrink-0 text-sm font-semibold tabular-nums text-foreground" data-testid={`${lineId}-amount`}>
                      <span dir="ltr">{formatCurrencyAmount(l.amount, l.currencyCode, lang)}</span>
                    </dd>
                  </div>
                );
              })}
            </dl>
            {o.repaidOnNextGeneration && (
              <p className="mt-1.5 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-repaid-${o.userId}`}>
                {t('Paid again at the new figure ({{points}} work points) by the next tournament pay generation.', { points: o.pointsAfter })}
              </p>
            )}
          </li>
        ))}
      </ul>

      {shownTotals.length > 0 && (
        <dl className="divide-y divide-border rounded-lg border border-border px-3">
          {shownTotals.map((tot) => (
            <div
              key={tot.currencyId}
              className="flex items-baseline justify-between gap-4 py-2"
              data-testid={`${testIdPrefix}-total-${tot.currencyCode}`}
            >
              <dt className="text-sm font-medium text-foreground">{t('Total taken back')}</dt>
              <dd className="shrink-0 text-base font-semibold tabular-nums text-foreground" data-testid={`${testIdPrefix}-total-${tot.currencyCode}-amount`}>
                <span dir="ltr">{formatCurrencyAmount(tot.pendingEntry + tot.nextPayroll, tot.currencyCode, lang)}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {showKindNotes && kinds.length > 0 && (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {kinds.includes('pendingEntry') && (
            <li><span className="font-medium text-foreground">{t('Removed from pending pay')}.</span> {t('Not on a payslip yet, so it is removed from their pending tournament pay.')}</li>
          )}
          {kinds.includes('nextPayroll') && (
            <li><span className="font-medium text-foreground">{t('Deducted on the next payroll')}.</span> {t('Already on a payslip, so it is deducted on their next payroll. The payslip itself is not changed.')}</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default ClawbackSummaryList;
